import React from 'react';
import { Columns, Column, Box } from '@nerdwallet/currency';
import lazy, { Hydrate } from '../../../lazy/lazy-universal';

import { FORM_NAME, EVENT_TYPE } from '../../../../lib/analytics-constants';
import {
  MORTGAGE,
  MORTGAGE_PAYMENT,
  MORTGAGE_STATE,
  MORTGAGE_CALCULATOR,
  MORTGAGE_PAYMENT_CALCULATOR,
  MORTGAGE_STATE_CALCULATOR,
} from '../analytics-constants';
import {
  PurchasePriceInput,
  DownPaymentInput,
  DownPaymentPercentInput,
} from '../inputs';
import type { MortgageCalculators } from '../types';

const ResultsHeader = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-mc-results-header' */ '../../../results-header/results-header'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

interface MCResultsHeaderProps {
  mortgageCalculator: MortgageCalculators;
}

const PURCHASE_PRICE_ANALYTICS = {
  enabled: true,
  payload: {
    bedrock: {
      eventType: EVENT_TYPE.HOME_VALUE_UPDATED,
      was_prefilled: true,
      form_name: FORM_NAME.MRT_CALCULATOR,
    },
  },
};

const DOWN_PAYMENT_ANALYTICS = {
  enabled: true,
  payload: {
    bedrock: {
      eventType: EVENT_TYPE.HOME_DOWN_PAYMENT_UPDATED,
      form_name: FORM_NAME.MRT_CALCULATOR,
      was_prefilled: true,
    },
  },
};

const DOWN_PAYMENT_PERCENT_ANALYTICS = {
  enabled: true,
  payload: {
    bedrock: {
      eventType: EVENT_TYPE.MORTGAGE_DOWN_PAYMENT_PERCENT_UPDATED,
      form_name: FORM_NAME.MRT_CALCULATOR,
      was_prefilled: true,
    },
  },
};

const RESULTS_HEADER_ANALYTICS = {
  [MORTGAGE]: MORTGAGE_CALCULATOR.RESULTS_HEADER,
  [MORTGAGE_PAYMENT]: MORTGAGE_PAYMENT_CALCULATOR.RESULTS_HEADER,
  [MORTGAGE_STATE]: MORTGAGE_STATE_CALCULATOR.RESULTS_HEADER,
};

const MCResultsHeader = ({
  mortgageCalculator,
}: MCResultsHeaderProps): JSX.Element => {
  const purchaseAnalytics = { ...PURCHASE_PRICE_ANALYTICS };
  const downPaymentAnalytics = { ...DOWN_PAYMENT_ANALYTICS };
  const downPaymentPercentAnalytics = { ...DOWN_PAYMENT_PERCENT_ANALYTICS };
  const ANALYTICS = RESULTS_HEADER_ANALYTICS[mortgageCalculator];
  purchaseAnalytics.payload.bedrock.form_name = ANALYTICS.FORM_NAME;
  downPaymentAnalytics.payload.bedrock.form_name = ANALYTICS.FORM_NAME;

  return (
    <ResultsHeader
      formName={ANALYTICS.FORM_NAME}
      entityName={ANALYTICS.ENTITY_NAME}
      sectionName={ANALYTICS.SECTION_NAME}
    >
      <PurchasePriceInput
        label="Home price"
        tooltip="What you pay for the home (if buying) or your home's current value (if refinancing)."
        analytics={purchaseAnalytics}
        fullWidth
      />
      <Columns alignY="top">
        <Column width="8">
          <DownPaymentInput
            label="Down payment"
            tooltip="The cash you pay upfront when buying a home as a percentage of your full loan amount."
            analytics={downPaymentAnalytics}
            fullWidth
          />
        </Column>
        <Column width="4">
          <Box marginTop="3-half">
            <DownPaymentPercentInput
              fullWidth
              analytics={downPaymentPercentAnalytics}
            />
          </Box>
        </Column>
      </Columns>
    </ResultsHeader>
  );
};

export default MCResultsHeader;
